import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Globe, PenLine, Clock3 } from 'lucide-react';
import { VendorDashboard, UserDashboard } from '../../components/MasterAdmin/dashboard';
import Card from '../../components/MasterAdmin/ui/Card';
import { fetchBlogDashboardStats, BlogDashboardStats } from '../../services/MasterAdmin/blogDashboardService';

const DashboardPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('users');
  const [blogStats, setBlogStats] = useState<BlogDashboardStats | null>(null);
  const [blogStatsLoading, setBlogStatsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setBlogStatsLoading(true);
        const stats = await fetchBlogDashboardStats();
        if (mounted) setBlogStats(stats);
      } catch (error) {
        console.error('Failed to load blog stats:', error);
        if (mounted) setBlogStats(null);
      } finally {
        if (mounted) setBlogStatsLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const showBlogCount = (value: number) => {
    if (blogStatsLoading) return 'Loading...';
    if (blogStats === null) return '—';
    return value.toLocaleString();
  };

  return (
    <div className="w-full min-w-0">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Health Blogs</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card
            title="Total Blogs"
            value={showBlogCount(blogStats?.total ?? 0)}
            loading={blogStatsLoading}
            icon={<FileText className="w-6 h-6 text-primary-600 dark:text-primary-400" />}
          />
          <Card
            title="Published (Live)"
            value={showBlogCount(blogStats?.published ?? 0)}
            loading={blogStatsLoading}
            icon={<Globe className="w-6 h-6 text-green-600 dark:text-green-400" />}
          />
          <Card
            title="Drafts"
            value={showBlogCount(blogStats?.draft ?? 0)}
            loading={blogStatsLoading}
            icon={<PenLine className="w-6 h-6 text-amber-600 dark:text-amber-400" />}
          />
          <Card
            title="Pending Approval"
            value={showBlogCount(blogStats?.pendingApproval ?? 0)}
            loading={blogStatsLoading}
            icon={<Clock3 className="w-6 h-6 text-orange-600 dark:text-orange-400" />}
          />
        </div>
      </div>

      <div className="-mx-1 mb-6 flex overflow-x-auto border-b border-gray-200 px-1 dark:border-gray-700 sm:mx-0 sm:px-0">
        <button
          className={`shrink-0 px-4 py-3 text-sm font-medium ${
            activeTab === 'users'
              ? 'border-b-2 border-primary-600 text-primary-600 dark:text-primary-400 dark:border-primary-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
          onClick={() => setActiveTab('users')}
        >
          User Dashboard
        </button>
        <button
          className={`shrink-0 px-4 py-3 text-sm font-medium ${
            activeTab === 'vendors'
              ? 'border-b-2 border-primary-600 text-primary-600 dark:text-primary-400 dark:border-primary-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
          onClick={() => setActiveTab('vendors')}
        >
          Vendor Dashboard
        </button>
        <div className="ml-auto flex shrink-0 items-center">
          <button
            type="button"
            className="shrink-0 px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
            onClick={() => navigate('/ambulance-off-hours-demand')}
          >
            Ambulance (off-hours)
          </button>
          <button
            type="button"
            className="shrink-0 px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
            onClick={() => navigate('/request-queue')}
          >
            Request Queue
          </button>
        </div>
      </div>

      {activeTab === 'vendors' ? <VendorDashboard /> : <UserDashboard />}
    </div>
  );
};

export default DashboardPage; 